import { useEffect, useRef } from 'react';
import { usePopup } from '../contexts/PopupContext';
import { supabase } from '../lib/supabase';

/**
 * Komponent obsługujący automatyczne otwieranie popupu z eventem
 * gdy użytkownik jest zalogowany i jest na /invite/:eventId
 * lub gdy użytkownik się właśnie zalogował z zapisanym eventId w sessionStorage
 * 
 * Dodatkowo tworzy zaproszenie w bazie danych jeśli użytkownik wszedł przez link zaproszenia
 */
function InviteHandler({ inviteEventId, session }) {
  const { openPopup } = usePopup();
  const popupOpenedRef = useRef(new Set()); // Używamy Set do śledzenia otwartych popupów
  const invitationCreatedRef = useRef(new Set()); // Używamy Set do śledzenia utworzonych zaproszeń

  // Funkcja do tworzenia zaproszenia w bazie danych
  const createInvitationFromLink = async (eventId, userId) => {
    if (invitationCreatedRef.current.has(`${eventId}-${userId}`)) {
      return; // Zaproszenie już zostało utworzone
    }

    try {
      // Sprawdź czy wydarzenie istnieje i pobierz host_id
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('id, host_id, is_cancelled, status')
        .eq('id', eventId)
        .single();

      if (eventError || !eventData) {
        console.error('Error loading event:', eventError);
        return;
      }

      // Nie tworzymy zaproszenia jeśli wydarzenie jest anulowane lub zakończone
      if (eventData.is_cancelled || eventData.status === 'ended') {
        return;
      }

      // Sprawdź czy użytkownik nie jest już uczestnikiem
      const { data: participantData } = await supabase
        .from('event_participants')
        .select('id')
        .eq('event_id', eventId)
        .eq('user_id', userId)
        .eq('status', 'active')
        .single();

      if (participantData) {
        // Użytkownik jest już uczestnikiem, nie tworzymy zaproszenia
        return;
      }

      // Sprawdź czy zaproszenie już istnieje
      const { data: existingInvitation } = await supabase
        .from('event_invitations')
        .select('id')
        .eq('event_id', eventId)
        .eq('invitee_id', userId)
        .single();

      if (existingInvitation) {
        // Zaproszenie już istnieje
        invitationCreatedRef.current.add(`${eventId}-${userId}`);
        return;
      }

      // Utwórz zaproszenie w bazie danych
      const { error: invitationError } = await supabase
        .from('event_invitations')
        .insert({
          event_id: eventId,
          inviter_id: eventData.host_id, // Host wydarzenia jest zapraszającym
          invitee_id: userId,
          status: 'pending'
        });

      if (invitationError) {
        // Jeśli błąd to nie unique constraint, loguj
        if (invitationError.code !== '23505') {
          console.error('Error creating invitation:', invitationError);
        }
      } else {
        invitationCreatedRef.current.add(`${eventId}-${userId}`);
      }
    } catch (error) {
      console.error('Error in createInvitationFromLink:', error);
    }
  };

  useEffect(() => {
    // Jeśli użytkownik jest zalogowany i mamy inviteEventId, utwórz zaproszenie i otwórz popup
    if (session?.user?.id && inviteEventId && !popupOpenedRef.current.has(inviteEventId)) {
      // Utwórz zaproszenie w bazie danych
      createInvitationFromLink(inviteEventId, session.user.id);

      // Otwórz popup po krótkim opóźnieniu, aby upewnić się że wszystko jest załadowane
      const timer = setTimeout(() => {
        openPopup('event-details', { eventId: inviteEventId }, 0);
        popupOpenedRef.current.add(inviteEventId);
        // Wyczyść URL
        window.history.replaceState({}, document.title, '/');
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [session, inviteEventId, openPopup]);

  useEffect(() => {
    // Sprawdź czy jest zapisane zaproszenie w sessionStorage (po zalogowaniu)
    if (session?.user?.id && !inviteEventId) {
      const pendingEventId = sessionStorage.getItem('pendingEventInvite');
      if (pendingEventId && !popupOpenedRef.current.has(pendingEventId)) {
        // Utwórz zaproszenie w bazie danych
        createInvitationFromLink(pendingEventId, session.user.id);

        // Otwórz popup po krótkim opóźnieniu
        const timer = setTimeout(() => {
          openPopup('event-details', { eventId: pendingEventId }, 0);
          popupOpenedRef.current.add(pendingEventId);
          // Wyczyść sessionStorage i URL
          sessionStorage.removeItem('pendingEventInvite');
          window.history.replaceState({}, document.title, '/');
        }, 300);

        return () => clearTimeout(timer);
      }
    }
  }, [session, inviteEventId, openPopup]);

  return null; // Ten komponent nie renderuje nic
}

export default InviteHandler;
